import DOMPurify from 'dompurify';
import { injectable } from 'inversify';

import {
  ImageVersion,
  checkImageStatus,
  flipImage,
  getVersionUrl,
  isDataURL,
} from '@desygner/ui-common-svg-utils';

@injectable()
export class SVGManager {
  public sanitizeSVG(content: string): string {
    return content
      .replace(/&nbsp;/g, ' ')
      .replace(/>[\t\n\s]+<(?!\/(?:tspan|text))/g, '><')
      .replace(/<\/tspan>\s*/g, '</tspan>')
      .replace(/\s*<tspan/g, '<tspan')
      .replace(/<\/text>\s*/g, '</text>')
      .replace(/\s*<text/g, '<text')
      .replace(/<\/g>\s*/g, '</g>')
      .replace(/<\/svg>\s*/g, '</svg>')
      .trim();
  }

  public createSvgElement(value: string): HTMLElement {
    const document = new DOMParser().parseFromString(value, 'image/svg+xml');

    return document.documentElement;
  }

  public getSVGDimensions(svg: HTMLElement) {
    const clonedSvg = svg.cloneNode(true) as HTMLElement;
    const DEFAULT_VIEWBOX = 1600;

    const width = Number(clonedSvg.getAttribute('width') ?? DEFAULT_VIEWBOX);
    const height = Number(clonedSvg.getAttribute('height') ?? DEFAULT_VIEWBOX);

    return {
      width,
      height,
    };
  }

  public setSVGViewbox(svg: HTMLElement) {
    const { height, width } = this.getSVGDimensions(svg);

    svg.setAttribute('viewBox', `0 0 ${width} ${height}`);

    return svg;
  }

  public async removeInvalidImagesFromSVG(svg: HTMLElement, isQa?: boolean) {
    const clonedSvg = svg.cloneNode(true) as HTMLElement;
    const images = clonedSvg.querySelectorAll('image');

    const imgPromises = Array.from(images).map(async (image) => {
      const href = image.getAttribute('xlink:href');
      const imageVersions = ['largeweb', 'web', 'original'] as ImageVersion[];

      if (href) {
        try {
          for await (const version of imageVersions) {
            const isImageAvailable = await checkImageStatus(
              getVersionUrl(href, version, isQa),
              1,
              0
            );

            if (!isImageAvailable) {
              const isFinalVersion = version === imageVersions[imageVersions.length - 1];

              if (isFinalVersion) {
                return { shouldRemove: true };
              }

              continue;
            }

            const imageUrl = getVersionUrl(href, version, isQa);

            image.setAttribute('xlink:href', imageUrl);

            return { shouldRemove: false };
          }
        } catch (error) {
          return {
            shouldRemove: true,
          };
        }
      }

      return {
        shouldRemove: false,
      };
    });

    const results = await Promise.allSettled(imgPromises);

    results.forEach((result, index) => {
      if (result.status === 'fulfilled' && result.value?.shouldRemove) {
        const image = images[index];

        image.remove();
      }
    });

    return clonedSvg;
  }

  public async processFlippedImages(svg: HTMLElement, isQa?: boolean): Promise<HTMLElement> {
    const clonedSvg = svg.cloneNode(true) as HTMLElement;
    const images = clonedSvg.querySelectorAll('image');

    const imagePromises = Array.from(images).map(async (image) => {
      const href = image.getAttribute('xlink:href');

      if (!href) return;

      const hasHorizontalFlip = image.classList.contains('image-flip-h');
      const hasVerticalFlip = image.classList.contains('image-flip-v');

      if (!hasHorizontalFlip && !hasVerticalFlip) return;

      try {
        let imageUrl = href;

        // If not already a data URL, convert to data URL first
        if (!isDataURL(href)) {
          const normalizedUrl = getVersionUrl(href, 'largeweb', isQa);

          const response = await fetch(normalizedUrl);

          if (!response.ok) return;

          const blob = await response.blob();

          imageUrl = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();

            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
        }

        // Apply flip transformation
        const flippedImageUrl = await flipImage(imageUrl, hasHorizontalFlip, hasVerticalFlip);

        image.setAttribute('xlink:href', flippedImageUrl);
      } catch (error) {
        console.error(`Error processing flipped image: ${href}`, error);
      }
    });

    await Promise.all(imagePromises);

    return clonedSvg;
  }

  private prepareAllLinksToOpenInNewTab = (node: Element) => {
    const videoType = node.getAttribute('data-video-type');

    if (videoType !== 'youtube') return;

    if ('target' in node) {
      node.setAttribute('target', '_blank');
    }

    if (
      !node.hasAttribute('target') &&
      (node.hasAttribute('xlink:href') || node.hasAttribute('href'))
    ) {
      node.setAttribute('xlink:show', 'new');
    }
  };

  private handleAfterSanitizeAttributes = (node: Element) => {
    this.prepareAllLinksToOpenInNewTab(node);
  };

  private cleanupHooksAfterSanitization = () => {
    DOMPurify.removeHook('afterSanitizeAttributes', this.handleAfterSanitizeAttributes);
  };

  public sanitizeDOM(value: string, extra_attrs: string[] = []): string {
    DOMPurify.addHook('afterSanitizeAttributes', this.handleAfterSanitizeAttributes);

    let sanitizedValue: string;

    try {
      sanitizedValue = DOMPurify.sanitize(value, {
        ADD_ATTR: [...extra_attrs],
        FORBID_TAGS: ['title'],
        ADD_TAGS: ['foreignobject', 'use'],
        HTML_INTEGRATION_POINTS: {
          foreignobject: true,
        },
      });
    } finally {
      this.cleanupHooksAfterSanitization();
    }
    return sanitizedValue;
  }

  public normalizeDimension({
    expectedWidth,
    originalHeight,
    originalWidth,
  }: {
    originalWidth: number;
    originalHeight: number;
    expectedWidth: number;
  }) {
    if (originalWidth < expectedWidth) {
      const ratio = originalHeight / originalWidth;
      const newHeight = expectedWidth * ratio;

      return {
        width: expectedWidth,
        height: newHeight,
      };
    }

    return {
      width: originalWidth,
      height: originalHeight,
    };
  }
}
